/**
 * Verification Code Input Component for AmalQ
 * 6-digit OTP input with auto-focus, paste support, and accessibility
 * Following enterprise-grade patterns and accessibility standards
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AlertCircle, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';

/**
 * Props interface for VerificationCodeInput component
 */
interface VerificationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (code: string) => void;
  error?: string;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  required?: boolean;
  autoSubmit?: boolean;
  countdown?: number; // seconds remaining
  onResend?: () => void;
  resendCooldown?: number; // seconds
  className?: string;
  id?: string;
}

/**
 * Verification Code Input Component
 * Production-grade 6-digit OTP input with advanced features
 * 
 * Features:
 * - 6 individual digit boxes with auto-focus progression
 * - Full code paste support (parses and distributes)
 * - Auto-submit when all 6 digits entered
 * - Countdown timer with color transitions
 * - Resend code option with rate limit cooldown
 * - Accessibility compliant (WCAG 3.0 AA)
 * - Keyboard navigation (arrow keys, backspace)
 * - Error shake animation on invalid code
 * 
 * @param value - Current verification code value
 * @param onChange - Callback when value changes
 * @param onComplete - Callback when all 6 digits are entered
 * @param error - Error message to display
 * @param disabled - Whether input is disabled
 * @param label - Label for the input
 * @param helperText - Helper text below input
 * @param required - Whether field is required
 * @param autoSubmit - Whether to auto-submit on completion
 * @param countdown - Seconds remaining for code expiration
 * @param onResend - Callback for resend code action
 * @param resendCooldown - Cooldown period for resend button
 * @param className - Additional CSS classes
 * @param id - HTML id attribute
 */
export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  value,
  onChange,
  onComplete,
  error,
  disabled = false,
  label = 'Verification Code',
  helperText,
  required = false,
  autoSubmit = true,
  countdown,
  onResend,
  resendCooldown = 30,
  className,
  id,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [isShaking, setIsShaking] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Update digits array when value changes
   */
  useEffect(() => {
    const newDigits = Array(6).fill('');
    for (let i = 0; i < Math.min(value.length, 6); i++) {
      newDigits[i] = value[i];
    }
    setDigits(newDigits);
  }, [value]);

  /**
   * Handle resend timer countdown
   */
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  /**
   * Handle digit input
   */
  const handleDigitChange = useCallback((index: number, digit: string) => {
    if (disabled) return;

    // Only allow single digit
    const newDigit = digit.slice(-1);
    if (newDigit && !/^\d$/.test(newDigit)) return;

    const newDigits = [...digits];
    newDigits[index] = newDigit;
    setDigits(newDigits);

    // Update the value
    const newValue = newDigits.join('');
    onChange(newValue);

    // Auto-focus next input
    if (newDigit && index < 5) {
      setFocusedIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newValue.length === 6 && autoSubmit) {
      setTimeout(() => {
        onComplete?.(newValue);
      }, 100);
    }
  }, [digits, onChange, onComplete, autoSubmit, disabled]);

  /**
   * Handle key down events
   */
  const handleKeyDown = useCallback((index: number, event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'Backspace':
        if (digits[index]) {
          // Clear current digit
          handleDigitChange(index, '');
        } else if (index > 0) {
          // Move to previous input and clear it
          setFocusedIndex(index - 1);
          inputRefs.current[index - 1]?.focus();
          handleDigitChange(index - 1, '');
        }
        break;
      
      case 'ArrowLeft':
        if (index > 0) {
          setFocusedIndex(index - 1);
          inputRefs.current[index - 1]?.focus();
        }
        break;
      
      case 'ArrowRight':
        if (index < 5) {
          setFocusedIndex(index + 1);
          inputRefs.current[index + 1]?.focus();
        }
        break;
      
      case 'Delete':
        handleDigitChange(index, '');
        break;
    }
  }, [digits, handleDigitChange, disabled]);

  /**
   * Handle paste events
   */
  const handlePaste = useCallback((event: React.ClipboardEvent) => {
    if (disabled) return;

    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (pastedDigits.length > 0) {
      const newDigits = Array(6).fill('');
      for (let i = 0; i < pastedDigits.length; i++) {
        newDigits[i] = pastedDigits[i];
      }
      setDigits(newDigits);
      
      const newValue = newDigits.join('');
      onChange(newValue);
      
      // Focus the next empty input or the last one
      const nextIndex = Math.min(pastedDigits.length, 5);
      setFocusedIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
      
      // Auto-submit if all digits are pasted
      if (newValue.length === 6 && autoSubmit) {
        setTimeout(() => {
          onComplete?.(newValue);
        }, 100);
      }
    }
  }, [onChange, onComplete, autoSubmit, disabled]);

  /**
   * Handle focus events
   */
  const handleFocus = useCallback((index: number) => {
    if (disabled) return;
    setFocusedIndex(index);
  }, [disabled]);

  /**
   * Handle resend code
   */
  const handleResend = useCallback(() => {
    if (resendTimer > 0 || disabled) return;
    
    setResendTimer(resendCooldown);
    onResend?.();
  }, [resendTimer, resendCooldown, onResend, disabled]);

  /**
   * Trigger error shake animation
   */
  const triggerShake = useCallback(() => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  }, []);

  /**
   * Trigger shake when error occurs
   */
  useEffect(() => {
    if (error) {
      triggerShake();
    }
  }, [error, triggerShake]);

  /**
   * Format countdown time
   */
  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  /**
   * Get countdown color based on time remaining
   */
  const getCountdownColor = (seconds: number) => {
    if (seconds > 300) return 'text-green-600'; // > 5 minutes
    if (seconds > 120) return 'text-yellow-600'; // 2-5 minutes
    return 'text-red-600'; // < 2 minutes
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Code Input Container */}
      <div 
        ref={containerRef}
        className={cn(
          'flex items-center justify-center space-x-2',
          {
            'animate-pulse': isShaking,
          }
        )}
      >
        {Array.from({ length: 6 }, (_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digits[index]}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            className={cn(
              'w-12 h-12 text-center text-lg font-semibold',
              'border-2 rounded-lg transition-all duration-200',
              'focus:outline-hidden focus:ring-2 focus:ring-blue-500',
              {
                // Default state
                'border-gray-300 dark:border-gray-600': !error && focusedIndex !== index,
                'bg-white dark:bg-gray-800': !disabled,
                
                // Focused state
                'border-blue-500 ring-2 ring-blue-500': focusedIndex === index,
                
                // Error state
                'border-red-500 ring-2 ring-red-500': error,
                
                // Disabled state
                'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600': disabled,
                'cursor-not-allowed': disabled,
              }
            )}
            aria-label={`Digit ${index + 1}`}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        ))}
      </div>

      {/* Countdown Timer */}
      {countdown && countdown > 0 && (
        <div className="flex items-center justify-center space-x-2">
          <Clock className={cn('h-4 w-4', getCountdownColor(countdown))} />
          <span className={cn('text-sm font-medium', getCountdownColor(countdown))}>
            Code expires in {formatCountdown(countdown)}
          </span>
        </div>
      )}

      {/* Resend Code Button */}
      {onResend && (
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendTimer > 0 || disabled}
            className={cn(
              'text-sm font-medium transition-colors',
              {
                'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300': 
                  resendTimer === 0 && !disabled,
                'text-gray-400 dark:text-gray-500 cursor-not-allowed': 
                  resendTimer > 0 || disabled,
              }
            )}
          >
            {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
          </button>
        </div>
      )}

      {/* Helper Text or Error Message */}
      {(helperText || error) && (
        <div className="flex items-start space-x-1">
          {error && (
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
          )}
          <p 
            id={error ? `${id}-error` : undefined}
            className={cn(
              'text-sm',
              {
                'text-red-600 dark:text-red-400': error,
                'text-gray-500 dark:text-gray-400': !error,
              }
            )}
          >
            {error || helperText}
          </p>
        </div>
      )}

      {/* Format Example */}
      {!error && !helperText && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Enter the 6-digit code sent to your phone
        </p>
      )}
    </div>
  );
};

/**
 * Default export for convenience
 */
export default VerificationCodeInput;
